import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "478",
  "slug": "what-is-timeguessr",
  "title": "What Is TimeGuessr? The Daily Game That Asks Where and When",
  "excerpt": "TimeGuessr adds a twist to the location-guessing genre: you guess not just where a photo was taken, but the year too. Here is how it works and why it has become a daily habit for so many.",
  "date": "2026-06-18",
  "category": "Community",
  "image": "https://images.unsplash.com/photo-1497942304796-b8bc2cc898f3?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwcGhvdG9ncmFwaHN8ZW58MXwwfHx8MTc4MTc3MDQyN3ww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "TimeGuessr is a free daily game that shows you historical and present-day photographs and asks you to guess both where and when each one was taken. You place a pin on a map and choose a year, earning points for accuracy on both, up to 50,000 across five rounds. It is part of the same shareable daily-game wave as Wordle and Worldle.",
  "content": [
    {
      "type": "paragraph",
      "text": "The location-guessing genre keeps finding clever new angles, and TimeGuessr is one of the most enjoyable twists yet. It takes the familiar challenge of working out where a photo was taken and adds a second dimension: you also have to guess when. That small addition changes the game completely, turning each round into a puzzle about both geography and history."
    },
    {
      "type": "heading",
      "text": "How TimeGuessr Works"
    },
    {
      "type": "paragraph",
      "text": "Each day, TimeGuessr serves up a set of photographs, a mix of historical images and modern ones, and for each photo you do two things. First, you place a pin on a world map to guess where it was taken. Second, you pick a year on a timeline to guess when. The game scores both your location and your date, so a great round means nailing the place and the era at once."
    },
    {
      "type": "paragraph",
      "text": "A standard game runs five rounds, and you can earn up to 50,000 points in total, with each round worth up to 10,000 split between getting the place right and getting the year right. Like other daily games, there is a fresh set every day and a tidy, spoiler-free way to share your score, which is a big part of why it spreads through group chats."
    },
    {
      "type": "heading",
      "text": "Why the Time Element Changes Everything"
    },
    {
      "type": "paragraph",
      "text": "Adding a date guess does something surprising: it makes you read photographs much more carefully. You start noticing the clues that betray an era as much as a place:"
    },
    {
      "type": "list",
      "items": [
        "Car models, fashion, and signage that anchor a photo to a decade.",
        "Film grain and colour, which hint at when and how a picture was taken.",
        "Buildings and infrastructure that did or did not exist at a given time.",
        "Historical events or technology visible in the frame."
      ]
    },
    {
      "type": "paragraph",
      "text": "It rewards a broad, casual knowledge of the world: a sense of when skylines filled in, when certain styles came and went, and how places have changed across the twentieth century and beyond."
    },
    {
      "type": "heading",
      "text": "Tips for a Better Score"
    },
    {
      "type": "paragraph",
      "text": "Because you are scored on both place and time, the trick is to treat them as two separate investigations within the same photo. A handful of habits make a real difference:"
    },
    {
      "type": "list",
      "items": [
        "Read the location clues first, just as you would in any geography game: landscape, architecture, vegetation, signage, and the language on any visible text.",
        "Then hunt for date clues independently. Cars are the single most reliable timestamp, since their styling changes sharply by decade. Clothing, hairstyles, and street advertising help too.",
        "Use the medium itself. Black-and-white or heavily faded colour usually pushes a photo earlier; crisp, saturated digital colour pulls it later.",
        "When a photo looks modern but you are unsure, lean slightly toward recent decades, since a large share of photographs in circulation are from the digital era.",
        "Do not let a confident date guess drag your location guess, or vice versa. They are scored separately, so commit fully to each."
      ]
    },
    {
      "type": "paragraph",
      "text": "The skill compounds over time. After a week or two you start to feel the era of a photo almost instantly, the same way regular players of location games come to recognise a country at a glance."
    },
    {
      "type": "heading",
      "text": "Part of the Daily Game Wave"
    },
    {
      "type": "paragraph",
      "text": "TimeGuessr belongs to the same family as Wordle, Worldle, and the other once-a-day puzzles that became a global habit. The formula is consistent and it works: one short challenge per day, the same for everyone, a clean score to compare, and no pressure to binge. It is a few minutes of fun you can fit into a coffee break, and the shared daily puzzle gives friends something to compare and compete over."
    }
  ]
};
